<template>
  <AnnDialog
    :model-value="props.modelValue"
    @update:modelValue="handleClose"
  >
    <div class="ann-login-dialog">
      <img
        class="ann-login-bg"
        :src="backgroundImg"
        alt=""
        aria-hidden="true"
      />
      <img
        class="ann-login-title"
        :src="titleImg"
        :alt="$t('ann2026.login.logintxt')"
      />

      <div class="ann-login-field ann-login-field--account">
        <label class="ann-login-label">
          {{ $t('ann2026.login.accountLabel') }}
        </label>
        <div class="ann-login-input-shell ann-login-input-shell--account">
          <input
            v-model="state.account"
            class="ann-login-input"
            type="text"
            :placeholder="$t('ann2026.login.emailPlaceholder')"
          />
        </div>
      </div>

      <div class="ann-login-field ann-login-field--code">
        <label class="ann-login-label">
          {{ $t('ann2026.login.codeLabel') }}
        </label>
        <div class="ann-login-code-row">
          <div class="ann-login-input-shell ann-login-input-shell--code">
            <input
              v-model="state.code"
              class="ann-login-input"
              type="text"
              maxlength="6"
              :placeholder="$t('ann2026.login.codePlaceholder')"
            />
          </div>
          <button
            class="ann-login-button ann-login-button--send"
            :class="{ 'ann-login-button--send-en': isEnglishLoginCopy }"
            :disabled="state.countdown > 0"
            @click="handleSendCode"
          >
            {{ state.countdown > 0 ? state.countdown : $t('ann2026.login.sendCode') }}
          </button>
        </div>
      </div>

      <div class="ann-login-actions">
        <button
          class="ann-login-button ann-login-button--cancel"
          @click="handleClose"
        >
          {{ $t('ann2026.login.cancel') }}
        </button>
        <button
          class="ann-login-button ann-login-button--submit"
          @click="handleLogin"
        >
          {{ $t('ann2026.login.submit') }}
        </button>
      </div>
      <AnnDialogClose
        :aria-label="$t('ann2026.login.cancel')"
        @close="handleClose"
      />
    </div>
  </AnnDialog>
</template>

<script setup>
/* eslint-disable */
import {
  computed,
  reactive,
  defineProps,
  defineEmits,
  watch,
  onUnmounted,
} from 'vue'
import { toastService } from '@/utils/toastService'
import { useI18n } from 'vue-i18n'
import ta from 'thinkingdata-browser'
import { useLog } from '@/composables/useActLog'
import AnnApi from '@/config/annActApi'
import AnnDialog from '../AnnDialog/index.vue'
import AnnDialogClose from '../AnnDialogClose/index.vue'
import backgroundImg from '@/assets/imgs/act/ann2026/popups/login/background.png'
import titleZh from '@/assets/imgs/act/ann2026/popups/login/title/zh.png'
import titleEn from '@/assets/imgs/act/ann2026/popups/login/title/en.png'
import titleVi from '@/assets/imgs/act/ann2026/popups/login/title/vi.png'
import titleId from '@/assets/imgs/act/ann2026/popups/login/title/id.png'
import titleTh from '@/assets/imgs/act/ann2026/popups/login/title/th.png'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'loginSuccess'])
const { t, locale } = useI18n()
const log = useLog()
const EN_LOGIN_LOCALES = ['en', 'sg', 'my', 'ph']
const isEnglishLoginCopy = computed(() =>
  EN_LOGIN_LOCALES.includes(locale.value),
)

const titleImg = computed(() => {
  switch (locale.value) {
    case 'hk':
    case 'tw':
      return titleZh
    case 'vi':
      return titleVi
    case 'id':
      return titleId
    case 'th':
      return titleTh
    default:
      return titleEn
  }
})

const state = reactive({
  account: '',
  code: '',
  countdown: 0,
  timer: null,
})

const clearCountdown = () => {
  if (state.timer) {
    clearInterval(state.timer)
    state.timer = null
  }
  state.countdown = 0
}

const handleClose = () => {
  emit('update:modelValue', false)
}

const startCountdown = () => {
  state.countdown = 60
  state.timer = setInterval(() => {
    state.countdown--
    if (state.countdown <= 0) {
      clearInterval(state.timer)
      state.timer = null
      state.countdown = 0
    }
  }, 1000)
}

const handleSendCode = async () => {
  if (!state.account) {
    toastService.show(t('ann2026.login.emailPlaceholder'))
    return
  }
  if (state.countdown > 0) return
  try {
    await AnnApi.sendMailCode({ uid: state.account.toUpperCase() })
    startCountdown()
    toastService.show(t('ann2026.login.codeSent'))
  } catch (e) {
    console.error(e)
  }
}

const handleLogin = async () => {
  if (!state.account) {
    toastService.show(t('ann2026.login.emailPlaceholder'))
    return
  }
  if (!state.code) {
    toastService.show(t('ann2026.login.codePlaceholder'))
    return
  }
  try {
    const res = await AnnApi.loginByMailCode({
      uid: state.account.toUpperCase(),
      code: state.code,
    })
    if (res && res.authorization) {
      // 端外账号登录
      const uid = res.roleInfo?.uid || ''
      if (uid) ta.login(uid)
      log.track('act_anni_login', { v1: 2 })
      emit('loginSuccess')
      emit('update:modelValue', false)
      toastService.show(t('ann2026.login.loginSuccess'))
    }
  } catch (e) {
    console.error(e)
    toastService.show(t('ann2026.login.codeError'))
  }
}

watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      clearCountdown()
      state.account = ''
      state.code = ''
    }
  },
)

onUnmounted(() => {
  clearCountdown()
})
</script>

<style lang="less" scoped>
.ann-login-dialog {
  --ann-close-top: -10px;
  --ann-close-right: -10px;

  position: relative;
  width: 696px;
  height: 557px;
  overflow: visible;
}

.ann-login-bg {
  position: absolute;
  inset: 0;
  width: 696px;
  height: 557px;
  pointer-events: none;
}

.ann-login-title {
  position: absolute;
  // 设计意图：标题向上探出弹窗顶部边缘。
  top: -30px;
  left: 50%;
  z-index: 2;
  display: block;
  // width: 5.72rem;
  max-width: calc(100% - 80px);
  // height: auto;
  transform: translateX(-50%);
  pointer-events: none;
  height: 100px;
}

.ann-login-field {
  position: absolute;
  left: 0;
  z-index: 2;
  width: 696px;
}

.ann-login-field--account {
  top: 181px;
}

.ann-login-field--code {
  top: 285px;
}

.ann-login-label {
  display: block;
  height: 24px;
  margin: 0 0 7px 96px;
  color: #fff;
  font-size: 20px;
  line-height: 24px;
  font-weight: 700;
  text-shadow: 0 2px 2px rgba(117, 22, 118, 0.45);
}

.ann-login-input-shell {
  position: relative;
  height: 60px;
  overflow: visible;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    z-index: 0;
    background-position: center;
    background-repeat: no-repeat;
    pointer-events: none;
  }
}

.ann-login-input-shell--account {
  width: 505px;
  margin-left: 96px;

  &::before {
    top: -6px;
    left: -6px;
    width: 517px;
    height: 72px;
    background-image: url('~@/assets/imgs/act/ann2026/popups/login/input-id.png');
    background-size: 517px 72px;
  }
}

.ann-login-code-row {
  display: flex;
  width: 496px;
  height: 60px;
  margin-left: 100px;
  column-gap: 14px;
}

.ann-login-input-shell--code {
  flex: none;
  width: 299px;

  &::before {
    top: -6.5px;
    left: -6.5px;
    width: 312px;
    height: 73px;
    background-image: url('~@/assets/imgs/act/ann2026/popups/login/input-code.png');
    background-size: 312px 73px;
  }
}

.ann-login-input {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 60px;
  margin: 0;
  padding: 0 22px;
  box-sizing: border-box;
  border: 0;
  outline: 0;
  background: transparent;
  color: #96246f;
  font-size: 20px;
  line-height: 60px;
  font-weight: 700;

  &::placeholder {
    color: rgba(150, 36, 111, 0.58);
  }
}

.ann-login-actions {
  position: absolute;
  top: 424px;
  left: 171px;
  z-index: 2;
  display: flex;
  width: 354px;
  height: 60px;
  column-gap: 32px;
}

.ann-login-button {
  position: relative;
  flex: none;
  height: 60px;
  margin: 0;
  padding: 0 10px;
  box-sizing: border-box;
  border: 0;
  outline: 0;
  background-color: transparent;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  box-shadow: none;
  appearance: none;
  color: #fff;
  font-size: 18px;
  line-height: 20px;
  font-weight: 700;
  text-align: center;
  cursor: pointer;
}

.ann-login-button--send {
  width: 223px;
  max-width: 223px;
  padding: 0 12px;
  overflow: hidden;
  overflow-wrap: anywhere;
  background-image: url('~@/assets/imgs/act/ann2026/popups/login/btn-sendcode.png');

  &:disabled {
    opacity: 0.65;
    cursor: default;
  }
}

.ann-login-button--send-en {
  font-size: 15px;
  line-height: 16px;
}

.ann-login-button--cancel,
.ann-login-button--submit {
  width: 161px;
  max-width: 161px;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.ann-login-button--cancel {
  background-image: url('~@/assets/imgs/act/ann2026/popups/login/btn-cancel.png');
}

.ann-login-button--submit {
  background-image: url('~@/assets/imgs/act/ann2026/popups/login/btn-submit.png');
  color: #9b2679;
}
</style>
